// Web Push in this browser. Contract: chess-backend-new docs/NOTIFICATIONS_FRONTEND_GUIDE.md.
// Every function that talks to the backend takes the `useApi()` object as its first argument.

const SUBSCRIPTIONS = "push/web-subscriptions";

export function urlBase64ToUint8Array(base64) {
    const padding = "=".repeat((4 - (base64.length % 4)) % 4);
    const raw = atob((base64 + padding).replaceAll("-", "+").replaceAll("_", "/"));
    return Uint8Array.from(raw, char => char.codePointAt(0));
}

export function isPushSupported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/** "default" | "granted" | "denied", or "unsupported" (iOS Safari outside the home screen has no Notification) */
export function notificationPermission() {
    return "Notification" in window ? Notification.permission : "unsupported";
}

/** iPhone/iPad in the browser: Web Push needs the site installed to the home screen (iOS 16.4+) */
export function isIosWithoutInstall() {
    const ua = navigator.userAgent;
    // iPadOS 13+ reports itself as a Mac
    const isIos = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    const isStandalone = window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true;
    return isIos && !isStandalone;
}

export function registerServiceWorker() {
    if (!("serviceWorker" in navigator)) {
        return Promise.resolve(null);
    }
    return navigator.serviceWorker.register("/sw.js");
}

/**
 * This browser's current subscription, or null. Uses getRegistration(), not `ready`:
 * `ready` never resolves when the worker failed to register, which would hang the caller.
 */
export async function getBrowserSubscription() {
    if (!isPushSupported()) {
        return null;
    }
    const registration = await navigator.serviceWorker.getRegistration();
    return registration ? registration.pushManager.getSubscription() : null;
}

/** "Is this browser on?": permission granted and a subscription exists */
export async function isBrowserPushOn() {
    return notificationPermission() === "granted" && (await getBrowserSubscription()) !== null;
}

/**
 * Must run from a click handler: browsers ignore permission prompts without a user gesture.
 * @returns {Promise<"on" | "denied" | "unsupported">}
 */
export async function enableBrowserPush(api, vapidPublicKey) {
    if (!isPushSupported()) {
        return "unsupported";
    }
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
        return "denied";
    }
    if (!(await navigator.serviceWorker.getRegistration())) {
        await registerServiceWorker();
    }
    // subscribe() needs an active worker
    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    const subscription = existing ?? await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
    try {
        await api.post(SUBSCRIPTIONS, subscription.toJSON());
    } catch (err) {
        // Otherwise the browser looks "on" while the server has nothing
        if (!existing) {
            await subscription.unsubscribe().catch(() => {});
        }
        throw err;
    }
    return "on";
}

/** Removes this browser on the server, then locally. A 404 means the server already forgot it. */
export async function disableBrowserPush(api) {
    const subscription = await getBrowserSubscription();
    if (!subscription) {
        return;
    }
    try {
        // useApi().delete passes options straight to fetch, so the body goes in as a string
        await api.delete(SUBSCRIPTIONS, { body: JSON.stringify({ endpoint: subscription.endpoint }) });
    } catch (err) {
        if (err?.status !== 404) {
            throw err;
        }
    }
    await subscription.unsubscribe();
}

/**
 * Logout: always drop the local subscription, even if the server call fails or hangs,
 * so the previous user stops getting pushes here. Resolves within `timeoutMs`.
 */
export function unsubscribeOnLogout(api, timeoutMs = 3000) {
    const work = (async () => {
        const subscription = await getBrowserSubscription();
        if (!subscription) {
            return;
        }
        await api.delete(SUBSCRIPTIONS, { body: JSON.stringify({ endpoint: subscription.endpoint }) }).catch(() => {});
        await subscription.unsubscribe();
    })().catch(err => console.warn("Push unsubscribe on logout failed", err));
    return Promise.race([work, new Promise(resolve => setTimeout(resolve, timeoutMs))]);
}

/**
 * Every app start after login: re-send the subscription. Covers browser key rotation and
 * account switches (the backend keys subscriptions by endpoint). Idempotent.
 */
export async function syncBrowserPush(api) {
    if (notificationPermission() !== "granted") {
        return;
    }
    const subscription = await getBrowserSubscription();
    if (subscription) {
        await api.post(SUBSCRIPTIONS, subscription.toJSON());
    }
}
